/**
 * AI Image API Service
 * Proxies AI image generation/editing through the backend to keep credentials
 * server-side. Provider-neutral: the backend selects the model provider.
 *
 * Requirements: 4.2, 5.3
 */

import { fetchWithAuthRetry } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Application-imposed prompt cap, not a provider limitation — keep in sync with
// author-api's AI_IMAGE_PROMPT_MAX_LENGTH (geminiImageService.ts).
export const AI_IMAGE_PROMPT_MAX_LENGTH = 4000;
export const AI_IMAGE_PROMPT_TOO_LONG_MESSAGE = `Description must be ${AI_IMAGE_PROMPT_MAX_LENGTH} characters or fewer`;

export interface ImageGenerationRequest {
  prompt: string;
  aspectRatio?: string;
}

export interface ImageEditRequest {
  image?: Blob;
  imageUrl?: string;
  prompt: string;
  aspectRatio?: string;
}

export interface ImageGenerationResponse {
  imageData: string;  // Base64 encoded image
  mimeType: string;
  text?: string;      // Model may include explanatory text
}

/**
 * Convert a Blob to base64 string
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Generate a new image from a text prompt via the backend AI provider
 * Requirements: 4.2
 */
export async function generateImage(
  request: ImageGenerationRequest
): Promise<ImageGenerationResponse> {
  if (!request.prompt.trim()) {
    throw new Error('Please enter a description for the image');
  }

  if (request.prompt.length > AI_IMAGE_PROMPT_MAX_LENGTH) {
    throw new Error(AI_IMAGE_PROMPT_TOO_LONG_MESSAGE);
  }

  const response = await fetchWithAuthRetry(`${API_BASE_URL}/ai/images/generate`, {
    method: 'POST',
    body: JSON.stringify({
      prompt: request.prompt,
      aspectRatio: request.aspectRatio,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to generate image' }));
    throw new Error(error.message || 'Failed to generate image');
  }

  return response.json();
}

/**
 * Edit an existing image via the backend AI provider
 * Requirements: 5.3
 */
export async function editImage(
  request: ImageEditRequest
): Promise<ImageGenerationResponse> {
  if (!request.prompt.trim()) {
    throw new Error('Please enter a description for the edit');
  }

  if (request.prompt.length > AI_IMAGE_PROMPT_MAX_LENGTH) {
    throw new Error(AI_IMAGE_PROMPT_TOO_LONG_MESSAGE);
  }

  const body: Record<string, string | undefined> = {
    prompt: request.prompt,
    aspectRatio: request.aspectRatio,
  };

  if (request.imageUrl) {
    body.imageUrl = request.imageUrl;
  } else if (request.image) {
    body.imageBase64 = await blobToBase64(request.image);
    let mimeType = request.image.type;
    if (!mimeType || mimeType === 'application/octet-stream') {
      mimeType = 'image/jpeg';
    }
    body.mimeType = mimeType;
  }

  const response = await fetchWithAuthRetry(`${API_BASE_URL}/ai/images/edit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to edit image' }));
    throw new Error(error.message || 'Failed to edit image');
  }

  return response.json();
}

/**
 * Convert base64 image data to a Blob
 */
export function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

/**
 * Convert base64 image data to a data URL for display
 */
export function base64ToDataUrl(base64: string, mimeType: string): string {
  return `data:${mimeType};base64,${base64}`;
}
